import type { DisplaySize } from './markupEditorCoordinates'
import type { MarkupPoint, MarkupRectangle } from './types'

export type MarkupLabelDisplaySize = DisplaySize

export type MarkupLabelPlacementKind = 'annotation' | 'zone'

export interface MarkupLabelPlacement {
  /** Text coordinates before the horizontal aspect-ratio compensation. */
  x: number
  y: number
  fontSize: number
  strokeWidth: number
  textScaleX: number
  /** Screen-pixel bounds used to choose and test the edge-safe placement. */
  left: number
  top: number
  width: number
  height: number
  label: string
}

export type MarkupLabelBounds = Pick<
  MarkupRectangle,
  'x_min' | 'y_min' | 'x_max' | 'y_max'
>

const MARK_LABEL_FONT_SIZE_PX = 14
const ZONE_LABEL_FONT_SIZE_PX = 13
const MARK_LABEL_GAP_PX = 10
const ZONE_LABEL_GAP_PX = 8
const LABEL_STROKE_WIDTH_PX = 3
const LABEL_CHARACTER_WIDTH = 1
const LABEL_ASCENT = 0.78

const positive = (value: number, fallback: number) =>
  Number.isFinite(value) && value > 0 ? value : fallback

/**
 * Places a compact screen-sized SVG label above its anchor where possible.
 * The returned text coordinates are paired with `transform: scale(textScaleX 1)`
 * so labels keep their screen dimensions in the normalized, non-square viewBox.
 */
export const placeMarkupLabel = (
  anchor: MarkupPoint,
  label: string,
  displaySize: MarkupLabelDisplaySize,
  kind: MarkupLabelPlacementKind = 'annotation',
  bounds?: MarkupLabelBounds
): MarkupLabelPlacement => {
  const width = positive(displaySize.width, 1)
  const height = positive(displaySize.height, 1)
  const baseFontSizePx =
    kind === 'zone' ? ZONE_LABEL_FONT_SIZE_PX : MARK_LABEL_FONT_SIZE_PX
  const gapPx = kind === 'zone' ? ZONE_LABEL_GAP_PX : MARK_LABEL_GAP_PX
  const maxTextWidthPx = Math.max(
    baseFontSizePx,
    width - LABEL_STROKE_WIDTH_PX * 2 - gapPx
  )
  const maxCharacters = Math.max(
    1,
    Math.floor(maxTextWidthPx / (baseFontSizePx * LABEL_CHARACTER_WIDTH))
  )
  const visibleLabel =
    kind === 'zone' && label.length > maxCharacters
      ? `${label.slice(0, Math.max(1, maxCharacters - 1))}…`
      : label
  const fontSizePx =
    kind === 'annotation' && label.length > maxCharacters
      ? Math.max(1, Math.min(baseFontSizePx, maxTextWidthPx / label.length))
      : baseFontSizePx
  const textWidthPx = Math.min(
    maxTextWidthPx,
    Math.max(
      fontSizePx,
      visibleLabel.length * fontSizePx * LABEL_CHARACTER_WIDTH
    )
  )
  const boxWidthPx = textWidthPx + LABEL_STROKE_WIDTH_PX * 2
  const boxHeightPx = fontSizePx + LABEL_STROKE_WIDTH_PX * 2
  const anchorX = Math.min(width, Math.max(0, anchor.x * width))
  const boundsLeft = Math.min(
    width,
    Math.max(0, (bounds?.x_min ?? anchor.x) * width)
  )
  const boundsTop = Math.min(
    height,
    Math.max(0, (bounds?.y_min ?? anchor.y) * height)
  )
  const boundsRight = Math.min(
    width,
    Math.max(0, (bounds?.x_max ?? anchor.x) * width)
  )
  const boundsBottom = Math.min(
    height,
    Math.max(0, (bounds?.y_max ?? anchor.y) * height)
  )
  const candidates = [
    { left: anchorX + gapPx, top: boundsTop - gapPx - boxHeightPx },
    {
      left: anchorX - gapPx - boxWidthPx,
      top: boundsTop - gapPx - boxHeightPx,
    },
    { left: boundsRight + gapPx, top: boundsTop },
    { left: boundsLeft - gapPx - boxWidthPx, top: boundsTop },
    { left: anchorX + gapPx, top: boundsBottom + gapPx },
    { left: anchorX - gapPx - boxWidthPx, top: boundsBottom + gapPx },
  ]
  const fits = ({ left, top }: { left: number; top: number }) =>
    left >= 0 &&
    top >= 0 &&
    left + boxWidthPx <= width &&
    top + boxHeightPx <= height
  const preferred = candidates.find(fits) ?? candidates[0]!
  const left = Math.min(width - boxWidthPx, Math.max(0, preferred.left))
  const top = Math.min(height - boxHeightPx, Math.max(0, preferred.top))
  const textLeft = left + LABEL_STROKE_WIDTH_PX
  const baseline = top + LABEL_STROKE_WIDTH_PX + fontSizePx * LABEL_ASCENT
  const textScaleX = height / width
  return {
    x: textLeft / width / textScaleX,
    y: baseline / height,
    fontSize: fontSizePx / height,
    strokeWidth: LABEL_STROKE_WIDTH_PX / height,
    textScaleX,
    left,
    top,
    width: boxWidthPx,
    height: boxHeightPx,
    label: visibleLabel,
  }
}
